import type { ProductDTO, ProductVariantDTO } from "@eg/shared/dto/product.dto";
import { fetchJSON } from "./api";

export type ProductSummary = {
  id: string;
  title: string;
  slug: string;
  description?: string | null;
  images: string[];
  createdAt?: string;
  likesCount?: number;
  likedByMe?: boolean;
  _count: { variants: number; likes?: number };
};

export const categoryIds = [
  "vaporizer",
  "grinder",
  "papers",
  "filterTips",
  "cones",
  "lighters",
  "storage",
  "accessories",
  "cleaning",
  "scales",
  "humidor",
  "assortment",
] as const;

export type CategoryId = (typeof categoryIds)[number];

export type EnrichedProduct = ProductSummary & {
  brand: string;
  category: CategoryId;
  badges: string[];
  variants: ProductVariantDTO[];
  minPriceCents: number | null;
  maxPriceCents: number | null;
  compareAtMinCents: number | null;
  currency: string | null;
  inStock: boolean;
  heroImage: string;
  hoverImage?: string;
  likesCount?: number;
};

export type DiscoveryFilters = {
  q?: string;
  categories: CategoryId[];
  brands: string[];
  availability: "all" | "in_stock";
  priceMin?: number;
  priceMax?: number;
  badges: string[];
  sort?: "newest" | "price-asc" | "price-desc" | "popular";
};

export type FacetOverview = {
  categories: Record<string, number>;
  brands: Record<string, number>;
  badges: Record<string, number>;
  priceRange: { min: number; max: number };
};

export async function fetchProductSummaries(
  q?: string,
  take = 48,
): Promise<ProductSummary[]> {
  const qs = new URLSearchParams();
  if (q) qs.set("q", q);
  qs.set("take", `${take}`);
  try {
    const init =
      typeof window === "undefined"
        ? process.env.NODE_ENV === "production"
          ? { cache: "force-cache" as const, next: { tags: ["products"] } }
          : { cache: "no-store" as const }
        : undefined;
    return await fetchJSON<ProductSummary[]>(
      `/products?${qs.toString()}`,
      init,
    );
  } catch (error) {
    console.error("Failed to load product summaries", error);
    return [];
  }
}

export async function fetchProductDetail(slug: string): Promise<ProductDTO> {
  const init =
    typeof window === "undefined"
      ? process.env.NODE_ENV === "production"
        ? {
            cache: "force-cache" as const,
            next: { tags: ["products", `product:${slug}`] },
          }
        : { cache: "no-store" as const }
      : undefined;
  return fetchJSON<ProductDTO>(`/products/${encodeURIComponent(slug)}`, init);
}

function normalizeText(value: string | undefined | null) {
  return value?.toLowerCase().trim() ?? "";
}

export function deriveCategory(slug: string, title?: string): CategoryId {
  const source = `${slug} ${title ?? ""}`;
  const normalized = source
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, " ")
    .trim();

  if (normalized.includes("vaporizer")) return "vaporizer";
  if (normalized.includes("grinder")) return "grinder";
  if (
    /\b(paper|papers|papes|longpapes|shortpapes|blattchen)\b/.test(
      normalized,
    ) ||
    /\brolling\s+papers?\b/.test(normalized)
  )
    return "papers";
  if (normalized.includes("filter") || normalized.includes("tip"))
    return "filterTips";
  if (normalized.includes("cone")) return "cones";
  if (normalized.includes("lighter") || normalized.includes("feuer"))
    return "lighters";
  if (normalized.includes("jar") || normalized.includes("storage"))
    return "storage";
  if (normalized.includes("tray") || normalized.includes("case"))
    return "accessories";
  if (normalized.includes("clean")) return "cleaning";
  if (normalized.includes("waage") || normalized.includes("scale"))
    return "scales";
  if (normalized.includes("humidor") || normalized.includes("befeuchter"))
    return "humidor";
  return "assortment";
}

const categoryParamMap: Record<string, CategoryId> = {
  vaporizer: "vaporizer",
  vaporizers: "vaporizer",
  grinder: "grinder",
  grinders: "grinder",
  papers: "papers",
  paper: "papers",
  "filter&tips": "filterTips",
  "filter&tipping": "filterTips",
  "filter tips": "filterTips",
  "filter & tips": "filterTips",
  filtertips: "filterTips",
  cones: "cones",
  cone: "cones",
  lighters: "lighters",
  feuerzeuge: "lighters",
  storage: "storage",
  aufbewahrung: "storage",
  accessories: "accessories",
  zubehor: "accessories",
  zubehör: "accessories",
  cleaning: "cleaning",
  reinigung: "cleaning",
  scales: "scales",
  waagen: "scales",
  humidor: "humidor",
  assortment: "assortment",
  sortiment: "assortment",
};

export function normalizeCategoryParam(value: string | null | undefined) {
  if (!value) return null;
  const normalized = value
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");
  const compact = normalized.replace(/\s+/g, " ");
  const noSpaces = compact.replace(/\s+/g, "");

  return categoryParamMap[compact] ?? categoryParamMap[noSpaces] ?? null;
}

export function deriveBrand(title: string) {
  const firstWord =
    title
      .split(/\s+/)
      .map((part) => part.replace(/[^a-z0-9äöüß]/gi, ""))
      .filter(Boolean)[0] ?? "EchtGrandios";
  return firstWord.length > 2
    ? firstWord[0].toUpperCase() + firstWord.slice(1)
    : "EchtGrandios";
}

function computeBadges(
  createdAt: string | undefined,
  variants: ProductVariantDTO[],
) {
  const badges: string[] = [];
  const hasSale = variants.some(
    (v) =>
      typeof v.compareAtCents === "number" && v.compareAtCents > v.priceCents,
  );
  if (hasSale) badges.push("sale");

  const created =
    createdAt && !Number.isNaN(Date.parse(createdAt))
      ? new Date(createdAt)
      : null;
  if (created) {
    const daysSince = (Date.now() - created.getTime()) / (1000 * 60 * 60 * 24);
    if (daysSince <= 30) badges.push("new");
  }

  badges.push("shipping");
  return Array.from(new Set(badges));
}

function buildPriceInfo(variants: ProductVariantDTO[]) {
  const prices = variants
    .map((v) => v.priceCents)
    .filter((v) => typeof v === "number");
  const comparePrices = variants
    .map((v) => v.compareAtCents)
    .filter((v): v is number => typeof v === "number");

  if (!prices.length)
    return {
      minPriceCents: null,
      maxPriceCents: null,
      compareAtMinCents: comparePrices[0] ?? null,
      currency: null,
    };

  const minPriceCents = Math.min(...prices);
  const maxPriceCents = Math.max(...prices);
  const compareAtMinCents = comparePrices.length
    ? Math.min(...comparePrices)
    : null;

  return {
    minPriceCents,
    maxPriceCents,
    compareAtMinCents,
    currency: variants.find((v) => v.currency)?.currency ?? "EUR",
  };
}

export function enrichProduct(
  summary: ProductSummary,
  detail: ProductDTO,
): EnrichedProduct {
  const category = deriveCategory(summary.slug, summary.title);
  const brand = deriveBrand(summary.title);
  const badges = computeBadges(summary.createdAt, detail.variants);
  const { minPriceCents, maxPriceCents, compareAtMinCents, currency } =
    buildPriceInfo(detail.variants);
  const inStock = detail.variants.some((v) => v.stock > 0);

  return {
    ...summary,
    description: detail.description ?? summary.description ?? "",
    images: detail.images?.length ? detail.images : (summary.images ?? []),
    brand,
    category,
    badges,
    variants: detail.variants,
    minPriceCents,
    maxPriceCents,
    compareAtMinCents,
    currency,
    inStock,
    heroImage: (detail.images ?? [])[0] ?? "",
    hoverImage: (detail.images ?? [])[1],
    likesCount: detail.likesCount ?? summary.likesCount ?? 0,
  };
}

export async function fetchEnrichedProducts(
  q?: string,
  take?: number,
): Promise<EnrichedProduct[]> {
  const summaries = await fetchProductSummaries(q, take);

  const enriched = await Promise.all(
    summaries.map(async (summary) => {
      try {
        const detail = await fetchProductDetail(summary.slug);
        return enrichProduct(summary, detail);
      } catch (error) {
        console.error("Failed to hydrate product", summary.slug, error);
        return null;
      }
    }),
  );

  return enriched.filter(Boolean) as EnrichedProduct[];
}

export function filterProductsByState(
  products: EnrichedProduct[],
  filters: DiscoveryFilters,
) {
  let filtered = [...products];
  const query = normalizeText(filters.q);

  if (query) {
    filtered = filtered.filter((p) => {
      const haystack = normalizeText(
        `${p.title} ${p.description ?? ""} ${p.brand} ${p.category}`,
      );
      return haystack.includes(query);
    });
  }

  if (filters.categories.length) {
    filtered = filtered.filter((p) => filters.categories.includes(p.category));
  }

  if (filters.brands.length) {
    filtered = filtered.filter((p) => filters.brands.includes(p.brand));
  }

  if (filters.badges.length) {
    filtered = filtered.filter((p) =>
      filters.badges.every((b) => p.badges.includes(b)),
    );
  }

  if (filters.availability === "in_stock") {
    filtered = filtered.filter((p) => p.inStock);
  }

  if (typeof filters.priceMin === "number") {
    filtered = filtered.filter(
      (p) => p.minPriceCents === null || p.minPriceCents >= filters.priceMin!,
    );
  }

  if (typeof filters.priceMax === "number") {
    filtered = filtered.filter(
      (p) => p.minPriceCents === null || p.minPriceCents <= filters.priceMax!,
    );
  }

  switch (filters.sort) {
    case "price-asc":
      filtered.sort((a, b) => (a.minPriceCents ?? 0) - (b.minPriceCents ?? 0));
      break;
    case "price-desc":
      filtered.sort((a, b) => (b.minPriceCents ?? 0) - (a.minPriceCents ?? 0));
      break;
    case "popular":
      filtered.sort((a, b) => b._count.variants - a._count.variants);
      break;
    default:
      filtered.sort((a, b) => {
        const aDate = a.createdAt ? Date.parse(a.createdAt) : 0;
        const bDate = b.createdAt ? Date.parse(b.createdAt) : 0;
        return bDate - aDate;
      });
  }

  return filtered;
}

export function buildFacets(products: EnrichedProduct[]): FacetOverview {
  const facets: FacetOverview = {
    categories: {},
    brands: {},
    badges: {},
    priceRange: { min: 0, max: 0 },
  };

  let min = Number.POSITIVE_INFINITY;
  let max = 0;

  for (const p of products) {
    facets.categories[p.category] = (facets.categories[p.category] ?? 0) + 1;
    facets.brands[p.brand] = (facets.brands[p.brand] ?? 0) + 1;
    for (const b of p.badges) {
      facets.badges[b] = (facets.badges[b] ?? 0) + 1;
    }
    if (typeof p.minPriceCents === "number") {
      min = Math.min(min, p.minPriceCents);
      max = Math.max(max, p.minPriceCents);
    }
  }

  facets.priceRange = {
    min: Number.isFinite(min) ? min : 0,
    max: Number.isFinite(max) ? max : 0,
  };

  return facets;
}
